/* eslint-disable @typescript-eslint/no-explicit-any */

import { Check, ChevronDown } from "lucide-react";

import { Tag as TagIcon } from "@phosphor-icons/react";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { Badge } from "@/components/ui/badge";
import { useLabels } from "@/hooks/use-labels";
import { useBulkUpdateMailboxLabels } from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import { LabelType } from "@/types/common";

const LabelCellRenderer = ({
  params,
  pageMetadata,
}: {
  params: any;
  pageMetadata: {
    page: number;
    limit: number;
    selectedLabels: string[];
    quickFilterText?: string;
  };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: updateLabels, isPending: isUpdating } =
    useBulkUpdateMailboxLabels();
  const { data: labels } = useLabels(1, 100, LabelType.MAILBOX);
  const [updatingLabelId, setUpdatingLabelId] = useState<string | null>(null);

  const { hasPermission } = usePermission();

  const renderLabels = () => {
    // Check if labels exist and handle accordingly
    if (!params.data.labels || params.data.labels.length === 0) {
      return <span className="text-xs text-muted-foreground">No labels</span>;
    }

    // Get the first label if it exists
    const firstLabel = params.data.labels[0];

    return (
      <>
        {firstLabel && (
          <Badge
            key={firstLabel._id}
            style={{ backgroundColor: firstLabel.color }}
            className="text-background text-xs py-0.5 px-1.5 shadow-none rounded-sm"
          >
            {firstLabel.name}
          </Badge>
        )}
        {params.data.labels.length > 1 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge className="text-accent-foreground bg-accent text-xs py-0.5 px-1.5 shadow-none rounded-sm">
                  +{params.data.labels.length - 1}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-black text-white">
                <div className="flex flex-col gap-1">
                  {params.data.labels.slice(1).map((label: any) => (
                    <div key={label._id} className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: label?.color }}
                      />
                      <span className="text-xs">{label?.name}</span>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  };

  if (!hasPermission("mailboxes:edit")) {
    return <div className="h-full flex items-center">{renderLabels()}</div>;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="inline-flex w-max min-w-40 justify-between h-8 px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors">
          <div className="flex items-center gap-1 flex-1 justify-start w-4/5">
            {renderLabels()}
          </div>
          <div className="flex items-center gap-1 justify-center w-1/5">
            <ChevronDown className="h-4 ml-1 opacity-50" />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {labels?.data?.map((label) => (
          <DropdownMenuItem
            key={label._id}
            className="flex items-center gap-2 py-1.5"
            disabled={isUpdating && updatingLabelId === label._id}
            onSelect={(e) => {
              e.preventDefault();
              const hasLabel = params.data.labels?.some(
                (l: any) => l._id === label._id
              );
              setUpdatingLabelId(label._id);
              updateLabels(
                {
                  ids: [params.data._id],
                  labelIds: [label._id],
                  operation: hasLabel ? "remove" : "add",
                  pageMetadata,
                },
                {
                  onSettled: () => {
                    setUpdatingLabelId(null);
                  },
                }
              );
            }}
          >
            <div className="flex items-center gap-2 flex-1">
              <TagIcon weight="fill" size={16} style={{ fill: label.color }} />
              <span className="text-sm">{label.name}</span>
            </div>
            {updatingLabelId === label._id && isUpdating ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              params.data.labels?.some?.((l: any) => l._id === label._id) && (
                <Check className="h-3.5 w-3.5" />
              )
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LabelCellRenderer;
